import type { TokensOutput, UserSummary } from 'chery-contracts';

const ACCESS_TOKEN_KEY = 'chery.accessToken';
const REFRESH_TOKEN_KEY = 'chery.refreshToken';
const USER_KEY = 'chery.user';

const USER_ROLES: readonly string[] = ['ADMIN', 'PM', 'OWNER', 'MANAGER'];

const readItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeItem = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); the session then lives only in memory.
  }
};

const removeItem = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to clean up.
  }
};

const isUserSummary = (value: unknown): value is UserSummary => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.role === 'string' &&
    USER_ROLES.includes(candidate.role) &&
    typeof candidate.mustChangePassword === 'boolean'
  );
};

/** The only module that touches `localStorage` for auth state. */
export const authTokenStore = {
  getAccessToken: (): string | null => readItem(ACCESS_TOKEN_KEY),

  getRefreshToken: (): string | null => readItem(REFRESH_TOKEN_KEY),

  getUser: (): UserSummary | null => {
    const raw = readItem(USER_KEY);

    if (!raw) {
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(raw);

      return isUserSummary(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  setSession: ({ accessToken, refreshToken, user }: TokensOutput): void => {
    writeItem(ACCESS_TOKEN_KEY, accessToken);
    writeItem(REFRESH_TOKEN_KEY, refreshToken);
    writeItem(USER_KEY, JSON.stringify(user));
  },

  setUser: (user: UserSummary): void => {
    writeItem(USER_KEY, JSON.stringify(user));
  },

  clear: (): void => {
    removeItem(ACCESS_TOKEN_KEY);
    removeItem(REFRESH_TOKEN_KEY);
    removeItem(USER_KEY);
  },

  hasSession: (): boolean => readItem(ACCESS_TOKEN_KEY) !== null,
};
